import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../lib/api';
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from '../../../components/common/ResponsiveTable';
import type {
  IndexingResult,
  SeoRow,
  SiteSeoAudit,
} from '../../../types/websiteWorkspace';
import { apiMessage } from './workspaceLogic';

export default function WebsiteSeoWorkspace({
  projectId,
  siteUrl,
  digest,
}: {
  projectId: string;
  siteUrl: string;
  digest: string;
}) {
  const cache = useQueryClient();
  const [audit, setAudit] = useState<SiteSeoAudit | null>(null);
  const [search, setSearch] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [drafts, setDrafts] = useState<
    Record<number, { title: string; description: string }>
  >({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [indexing, setIndexing] = useState<Record<number, IndexingResult>>({});
  const [message, setMessage] = useState('');
  const stale = !!audit && audit.digest !== digest;
  const check = useMutation({
    mutationFn: async () =>
      (
        await api.get<SiteSeoAudit>(
          `/api/website-builder/projects/${projectId}/seo-audit/`,
        )
      ).data,
    onSuccess: (data) => {
      setAudit(data);
      setDrafts({});
      setSelected(new Set());
      setMessage('');
    },
  });
  const inspect = useMutation({
    mutationFn: async (id: number) => ({
      id,
      data: (
        await api.post<IndexingResult>(
          `/api/website-builder/projects/${projectId}/pages/${id}/indexing/`,
          {},
        )
      ).data,
    }),
    onSuccess: ({ id, data }) => setIndexing((old) => ({ ...old, [id]: data })),
  });
  const apply = useMutation({
    mutationFn: async () => {
      let saved = 0;
      const failures: string[] = [];
      for (const row of audit?.pages || []) {
        if (
          !row.id ||
          !selected.has(row.id) ||
          !row.updated_at ||
          !drafts[row.id]
        )
          continue;
        try {
          await api.websiteBuilder.updatePage(projectId, row.id, {
            base_updated_at: row.updated_at,
            meta_title: drafts[row.id].title,
            meta_description: drafts[row.id].description,
          });
          saved++;
        } catch (error) {
          failures.push(`${row.title}: ${apiMessage(error, 'Could not save')}`);
        }
      }
      return { saved, failures };
    },
    onSuccess: ({ saved, failures }) => {
      setMessage(
        `${saved} page(s) saved to draft. ${failures.join(' ')} Run the audit again before making more changes.`,
      );
      setSelected(new Set());
      cache.invalidateQueries({ queryKey: ['website-workbench', projectId] });
      cache.invalidateQueries({
        queryKey: ['website-builder-project', projectId],
      });
      cache.invalidateQueries({
        queryKey: ['website-builder-page', projectId],
      });
    },
  });
  const change = (row: SeoRow, key: 'title' | 'description', value: string) => {
    if (!row.id) return;
    setDrafts((old) => ({
      ...old,
      [row.id!]: {
        title: old[row.id!]?.title ?? row.meta_title ?? '',
        description: old[row.id!]?.description ?? row.meta_description ?? '',
        [key]: value,
      },
    }));
  };
  const columns: ResponsiveColumn<SeoRow>[] = [
    {
      key: 'page',
      header: 'Page',
      mobileVariant: 'header',
      render: (row) => (
        <Stack spacing={1}>
          <Typography fontWeight="lg">
            {row.title || row.slug || 'Home'}
          </Typography>
          <Typography level="body-xs">/{row.slug}</Typography>
          {row.id && (
            <Checkbox
              label="Include in save"
              disabled={stale || !drafts[row.id] || apply.isPending}
              checked={selected.has(row.id)}
              onChange={(event) =>
                setSelected((old) => {
                  const next = new Set(old);
                  if (event.target.checked) next.add(row.id!);
                  else next.delete(row.id!);
                  return next;
                })
              }
            />
          )}
        </Stack>
      ),
    },
    {
      key: 'issues',
      header: 'Saved-content checks',
      mobileVariant: 'body',
      render: (row) => (
        <Stack spacing={1}>
          {row.issues.length ? (
            row.issues.map((issue) => (
              <Typography key={issue} level="body-sm">
                • {issue}
              </Typography>
            ))
          ) : (
            <Chip color="success" size="sm">
              No issues in these checks
            </Chip>
          )}
          {row.suggestions.map((link) => (
            <Typography key={link.url} level="body-xs">
              Suggested link: {link.title} ({link.url})
            </Typography>
          ))}
        </Stack>
      ),
    },
    {
      key: 'metadata',
      header: 'Review metadata',
      mobileVariant: 'body',
      render: (row) => (
        <Stack spacing={1} sx={{ minWidth: { md: 280 } }}>
          {row.id && (
            <>
              <Input
                aria-label={`Title for ${row.title}`}
                value={drafts[row.id]?.title ?? row.meta_title ?? ''}
                onChange={(event) => change(row, 'title', event.target.value)}
                disabled={apply.isPending}
                slotProps={{ input: { maxLength: 255 } }}
              />
              <Input
                aria-label={`Description for ${row.title}`}
                value={
                  drafts[row.id]?.description ?? row.meta_description ?? ''
                }
                onChange={(event) =>
                  change(row, 'description', event.target.value)
                }
                disabled={apply.isPending}
                slotProps={{ input: { maxLength: 500 } }}
              />
              <Button
                size="sm"
                variant="plain"
                disabled={apply.isPending}
                onClick={() =>
                  setDrafts((old) => ({
                    ...old,
                    [row.id!]: {
                      title: row.suggested_title || '',
                      description: row.suggested_description || '',
                    },
                  }))
                }
              >
                Use suggestion for review
              </Button>
            </>
          )}
          <Box
            sx={{ bgcolor: 'background.level1', p: 1.5, borderRadius: 'sm' }}
          >
            <Typography level="body-xs">
              {siteUrl || 'Your domain'}/{row.slug}
            </Typography>
            <Typography color="primary">
              {(row.id && drafts[row.id]?.title) || row.meta_title || row.title}
            </Typography>
            <Typography level="body-sm">
              {(row.id && drafts[row.id]?.description) ||
                row.meta_description ||
                'Add a description'}
            </Typography>
            <Typography level="body-xs" sx={{ mt: 1 }}>
              Search-result preview — Google may choose different text.
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      key: 'index',
      header: 'Google indexing',
      render: (row) => (
        <Stack spacing={1}>
          <Typography level="body-sm">
            {row.id && indexing[row.id]
              ? indexing[row.id].message ||
                `${indexing[row.id].verdict || indexing[row.id].status}: ${indexing[row.id].coverage || ''}`
              : 'Not checked'}
          </Typography>
          {row.id && (
            <>
              <Button
                size="sm"
                variant="outlined"
                disabled={inspect.isPending}
                onClick={() => inspect.mutate(row.id!)}
              >
                Check indexing
              </Button>
              {indexing[row.id] && (
                <Typography level="body-xs">
                  Checked{' '}
                  {new Date(indexing[row.id].checked_at).toLocaleString()}
                </Typography>
              )}
            </>
          )}
        </Stack>
      ),
    },
  ];
  const rows = (audit?.pages || []).filter(
    (row) =>
      (!issuesOnly || row.issues.length) &&
      `${row.title} ${row.slug} ${row.issues.join(' ')}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  return (
    <Stack spacing={2}>
      <Typography level="h3">Site SEO work queue</Typography>
      <Typography>
        Audit saved drafts, review metadata changes, then publish from the site
        manager. Indexing checks describe the published page.
      </Typography>
      <CollapsibleFilters><Box
        sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <Button
          loading={check.isPending}
          disabled={apply.isPending}
          onClick={() => {
            if (
              !Object.keys(drafts).length ||
              window.confirm(
                'Discard the metadata suggestions you have edited and rerun the audit?',
              )
            )
              check.mutate();
          }}
        >
          Audit saved site
        </Button>
        <Input
          placeholder="Search pages or issues"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Checkbox
          label="Only pages with issues"
          checked={issuesOnly}
          onChange={(event) => setIssuesOnly(event.target.checked)}
        />
        <Button
          variant="outlined"
          loading={apply.isPending}
          disabled={!selected.size || stale || check.isPending}
          onClick={() => apply.mutate()}
        >
          Save {selected.size} reviewed draft(s)
        </Button>
      </Box></CollapsibleFilters>
      {(check.isError || inspect.isError) && (
        <Alert color="danger">
          {apiMessage(
            check.error || inspect.error,
            'The check failed. Please retry.',
          )}
        </Alert>
      )}
      {message && <Alert>{message}</Alert>}
      {stale && (
        <Alert color="warning">
          The saved draft changed since this audit. Rerun it before applying
          metadata edits.
        </Alert>
      )}
      {audit ? (
        <>
          <Typography level="body-sm">
            {audit.issue_count} issues · checked{' '}
            {new Date(audit.checked_at).toLocaleString()}
          </Typography>
          <ResponsiveTable
            rows={rows}
            columns={columns}
            getRowKey={(row) => row.slug || '/'}
            emptyState={<Typography>No pages match these filters.</Typography>}
          />
        </>
      ) : (
        <Alert color="neutral">
          Not checked yet. Run the audit to see saved-content results across
          your site.
        </Alert>
      )}
    </Stack>
  );
}
